import React, { useState } from "react";
import { Box, Button, Typography, Alert } from "@mui/material";
import { Link } from "react-router-dom";
import { Form, Input, FormField } from "semantic-ui-react";
import customHttp from "../../services/customHTTP";
import PageTitle from "../../components/PageTitle/PageTitle";
import Swal from "sweetalert2";
import "./ForgotPassword.css";
import { useAuth } from "../../context/authContext";
import AuthedUser from "../AuthedUser/AuthedUser";
import { ASSETS_BASE_URL } from "../../config";

const ForgotPassword = () => {
	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState("If an account with this email exists, a password reset link will be sent.");
	const [emailError, setEmailError] = useState("");

	const { isAuthenticated } = useAuth();

	const AuthPageStyles = {
		backgroundImage: `url('${ASSETS_BASE_URL}/home_bg2.jpg')`,
		backgroundSize: "cover",
		backgroundPosition: "center",
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!email) {
			setEmailError("Email is required");
			return;
		}
		setEmailError("");
		setLoading(true);

		const sendPasswordEmail = async () => {
			try {
				const req_body = {
					userId: email,
				};
				const response = await customHttp.post("/users/forgotpassword", req_body);

				if (response.status === 200) {
					const text =
						response.data?.message ||
						"If the account exists, a password reset email will be sent.";
					Swal.fire({
						icon: "success",
						title: "Sent",
						text,
					});
					setMessage(text);
				}
			} catch (err) {
				const status = err?.response?.status;
				const msg =
					err?.response?.data?.message ||
					err?.response?.data?.error_message ||
					err?.message ||
					"Failed to send password reset link. Please try again.";
				if (status === 429) {
					Swal.fire({
						icon: "warning",
						title: "Please wait",
						text: msg,
					});
				} else {
					Swal.fire({
						icon: "error",
						title: "Error",
						text: msg,
					});
				}
				setEmailError(status === 429 ? "" : "An error occurred. Please try again.");
			} finally {
				setLoading(false);
			}
		};

		sendPasswordEmail();
	};

	if (isAuthenticated) {
		return <AuthedUser />;
	}

	return (
		<Box className="login-container" sx={AuthPageStyles}>
			<Box className="login-form-container">
				<img src="/logo.png" alt="Logo" className="login-logo" />
				<div className="form-container">
					<PageTitle title="Forgot Password" />
					<Typography variant="body" textAlign="center" marginTop="15px">
						Enter your email to receive a password reset link.
					</Typography>
					{emailError && (
						<Alert severity="error" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{emailError}
						</Alert>
					)}
					<Form className="login-form" onSubmit={handleSubmit}>
						<Form.Field required error={!!emailError}>
							<Input
								placeholder="Email"
								type="email"
								className="login-input"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
						</Form.Field>
						<div className="login-button">
							<Button
								variant="contained"
								color="primary"
								className="button-primary"
								type="submit"
								sx={{ minWidth: "120px" }}
								disabled={loading}>
								{loading ? "Sending..." : "Reset Password"}
							</Button>
						</div>
					</Form>
					<Typography variant="body2" color="textSecondary" textAlign="center" marginTop="15px">
						{message}
					</Typography>
				</div>
				<Link to="/login" className="login-link">
					Back to Login
				</Link>
			</Box>
		</Box>
	);
};

export default ForgotPassword;
